import { Injector, inject } from '@angular/core';
import { Query, injectQuery } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { MaintenanceStateInfo } from '@mp/system/maintenance/domain';

import { MaintenanceModeSignalService, MaintenanceStateService } from '../services';

import { calculateMaintenanceStateRefetchInterval } from './maintenance-state-refetch-interval';
import { maintenanceStateQueryKey } from './maintenance-state.query-keys';

type MaintenanceStateQuery = Query<
  MaintenanceStateInfo,
  Error,
  MaintenanceStateInfo,
  typeof maintenanceStateQueryKey
>;

/**
 * Polls the maintenance state of the current login.
 *
 * Gepollt wird durchgehend, auch ohne offengelegtes Fenster: der markierte `x-mediio-maintenance`-503 sperrt
 * nur, eine Ankündigung schiebt der Server nicht nach - sie wird ausschließlich beim Nachfragen sichtbar.
 * Die Taktung steht in `calculateMaintenanceStateRefetchInterval`.
 * @param injector The injector to run the query in.
 * @returns The maintenance state query.
 */
export function injectMaintenanceStateQuery(injector: Injector = inject(Injector)) {
  const maintenanceStateService = injector.get(MaintenanceStateService);
  const maintenanceModeSignalService = injector.get(MaintenanceModeSignalService);

  return injectQuery(
    () => ({
      queryKey: maintenanceStateQueryKey,
      queryFn: () => lastValueFrom(maintenanceStateService.getMaintenanceState()),
      /**
       * Die kurze Frische entprellt nur die Auslöser gegeneinander: mit `refetchOnWindowFocus` würde jeder
       * Wechsel in den Tab einen zweiten vollständigen Poll Millisekunden nach dem Intervall-Poll auslösen.
       * Die Sperre bleibt davon unberührt - sie hängt nicht am Cache, sondern am markierten
       * `x-mediio-maintenance`-503, und jede Invalidierung holt den Zustand trotz Frische neu.
       */
      staleTime: 5_000,
      // isLockRequired kommt bewusst aus dem Marker (503) und nicht aus stateInfo.state === Active: der
      // Marker ist die tatsächliche Sperre dieses Benutzers, stateInfo kann - gerade wenn der Takt gerade
      // deshalb zu grob war - veraltet sein.
      refetchInterval: (query: MaintenanceStateQuery) =>
        calculateMaintenanceStateRefetchInterval(
          query.state.data ?? null,
          query.state.dataUpdatedAt,
          maintenanceModeSignalService.lockedMarker() !== null,
        ),
    }),
    { injector },
  );
}
